import { Message } from '@/types/posit';

const FAILED_MESSAGES_KEY = 'posit_failed_messages';
const VOICE_CACHE_KEY = 'posit_voice_cache';
const MAX_FAILED_MESSAGES = 50;
const CLEANUP_INTERVAL = 24 * 60 * 60 * 1000; // 24 hours

interface FailedMessage extends Message {
  originalError: string;
  failedAt: number;
  positId: string;
}

interface VoiceCacheItem {
  messageId: string;
  audioBlob: string; // base64 encoded
  timestamp: number;
}

class MessagePersistence {
  private voiceCache = new Map<string, Blob>();

  // Save failed message to localStorage
  saveFailedMessage(message: Message, positId: string, error: string): void {
    try {
      const failedMessages = this.getFailedMessages();
      
      const failedMessage: FailedMessage = {
        ...message,
        originalError: error,
        failedAt: Date.now(),
        positId,
        status: 'failed',
        error
      };

      failedMessages.push(failedMessage);
      
      // Keep only recent failed messages
      const recentMessages = failedMessages
        .sort((a, b) => b.failedAt - a.failedAt)
        .slice(0, MAX_FAILED_MESSAGES);

      localStorage.setItem(FAILED_MESSAGES_KEY, JSON.stringify(recentMessages));
      
    } catch (error) {
    }
  }

  // Get all failed messages
  getFailedMessages(): FailedMessage[] {
    try {
      const stored = localStorage.getItem(FAILED_MESSAGES_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      return [];
    }
  }

  // Get failed messages for specific posit
  getFailedMessagesForPosit(positId: string): FailedMessage[] {
    return this.getFailedMessages().filter(msg => msg.positId === positId);
  }

  // Remove a failed message (after successful retry)
  removeFailedMessage(messageId: string): void {
    try {
      const failedMessages = this.getFailedMessages();
      const filtered = failedMessages.filter(msg => msg.id !== messageId);
      localStorage.setItem(FAILED_MESSAGES_KEY, JSON.stringify(filtered));
      
    } catch (error) {
    }
  }

  // Store voice audio blob temporarily
  storeVoiceBlob(messageId: string, audioBlob: Blob): void {
    this.voiceCache.set(messageId, audioBlob);
  }

  // Get voice audio blob
  getVoiceBlob(messageId: string): Blob | null {
    return this.voiceCache.get(messageId) || null;
  }

  // Remove voice blob after successful processing
  removeVoiceBlob(messageId: string): void {
    this.voiceCache.delete(messageId);
  }

  // Cleanup old data
  cleanup(): void {
    try {
      const cutoff = Date.now() - CLEANUP_INTERVAL;
      const failedMessages = this.getFailedMessages();
      const recentMessages = failedMessages.filter(msg => msg.failedAt > cutoff);
      
      if (recentMessages.length !== failedMessages.length) {
        localStorage.setItem(FAILED_MESSAGES_KEY, JSON.stringify(recentMessages));
      }
    } catch (error) {
    }
  }

  // Check if message is retryable
  isRetryable(message: FailedMessage): boolean {
    const retryCount = message.retryCount || 0;
    const maxRetries = 3;
    const timeSinceFailure = Date.now() - message.failedAt;
    const minRetryDelay = 5000; // 5 seconds

    return retryCount < maxRetries && timeSinceFailure > minRetryDelay;
  }

  // Calculate next retry delay (exponential backoff)
  getRetryDelay(retryCount: number): number {
    const baseDelay = 5000; // 5 seconds
    return Math.min(baseDelay * Math.pow(2, retryCount), 60000); // Max 1 minute
  }
}

export const messagePersistence = new MessagePersistence();

// Auto-cleanup on load
messagePersistence.cleanup(); 